import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { ToastType } from '@components/common/Toast/Toast';
import { useCustomQuery } from '@hooks/core/useCustomQuery';
import { getUniqueId } from '@utils/common';
import { QueryKeys } from '@constants/queryKeys';

const TOASTS_QUERY_KEY = [QueryKeys.TOASTS];

type ToastItem = {
  id: string;
  content: string;
  type: ToastType;
  duration?: number;
};

/** Toasts shared through the query cache — rendered by `Toasts`. */
export const useToast = () => {
  const queryClient = useQueryClient();

  const { data: toasts = [] } = useCustomQuery<ToastItem[]>(
    TOASTS_QUERY_KEY,
    () => queryClient.getQueryData<ToastItem[]>(TOASTS_QUERY_KEY) ?? [],
    { initialData: [], staleTime: Infinity }
  );

  // Stable references: consumers keep `open` in effect deps.
  const open = useCallback(
    (content: string, type = ToastType.SUCCESS, duration?: number) => {
      queryClient.setQueryData<ToastItem[]>(TOASTS_QUERY_KEY, (prevData) => [
        ...(prevData ?? []),
        { id: getUniqueId(), content, type, duration },
      ]);
    },
    [queryClient]
  );

  const close = useCallback(
    (id: string) => {
      queryClient.setQueryData<ToastItem[]>(TOASTS_QUERY_KEY, (prevData) =>
        (prevData ?? []).filter((toast) => toast.id !== id)
      );
    },
    [queryClient]
  );

  const openError = useCallback(
    (content: string) => open(content, ToastType.ERROR),
    [open]
  );

  const openSuccess = useCallback(
    (content: string) => open(content, ToastType.SUCCESS),
    [open]
  );

  return { toasts, open, openError, openSuccess, close };
};
